import { format } from 'date-fns';
import { Frown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  Card,
  CardHeader,
  CardDescription,
  CardTitle,
  CardContent,
} from '@/Domain/components/Shared/Card/Card';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/Domain/components/Shared/Chart/chart';
import { ChartPlaceholder } from '@/Domain/components/Shared/ChartPlaceholder';

type Props = {
  data: any[];
  isLoading: boolean;
};

export const SentimentAnalysisChart = ({ data, isLoading }: Props) => {
  const { t } = useTranslation('insights_page', { keyPrefix: 'ai_insights' });

  const hasData = data && data.length > 0;

  const chartConfig = {
    positive: { label: t('sentiment_analysis.positive'), color: 'var(--chart-positive)' },
    neutral: { label: t('sentiment_analysis.neutral'), color: 'var(--chart-neutral)' },
    negative: { label: t('sentiment_analysis.negative'), color: 'var(--chart-negative)' },
  } as const;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('sentiment_analysis.title')}</CardTitle>
        <CardDescription>{t('sentiment_analysis.distribution_over_time')}</CardDescription>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <ChartPlaceholder
            icon={Frown}
            titleTranslationKey="no_data_in_range.title"
            descriptionTranslationKey="no_data_in_range.description"
            spacing="small"
          />
        ) : (
          <ChartContainer
            config={chartConfig}
            className={`h-60 w-full ${isLoading ? 'animate-pulse' : ''}`}
          >
            <BarChart accessibilityLayer data={data} margin={{ left: 0, right: 0 }}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) => format(new Date(value), 'dd MMM')}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                allowDecimals={false}
                width={36}
              />
              <ChartTooltip
                cursor={false}
                wrapperStyle={{ whiteSpace: 'normal' }}
                labelFormatter={(value) => format(new Date(value), 'dd MMM yyyy')}
                content={<ChartTooltipContent indicator="dot" className="w-auto" />}
              />
              <ChartLegend
                content={({ payload }) => (
                  <ChartLegendContent payload={[...(payload ?? [])].reverse()} />
                )}
              />
              <Bar
                dataKey="negative"
                stackId="a"
                fill="var(--color-negative)"
                radius={[0, 0, 4, 4]}
              />
              <Bar
                dataKey="neutral"
                stackId="a"
                fill="var(--color-neutral)"
                radius={[0, 0, 0, 0]}
              />
              <Bar
                dataKey="positive"
                stackId="a"
                fill="var(--color-positive)"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};
